import React, { useState } from 'react';
import useDictionary from '../../hooks/useDictionary';
import Dictionary from '../../utils/dictionary';
import { fillPhonePlaceholder } from '../../utils/maintenance';
import { SESSION_STORAGE_PRE_OUTAGE_BANNER_DISMISSED } from '../../utils/constants';
import useStyles from './useStyles';

const isDismissed = () =>
  sessionStorage.getItem(SESSION_STORAGE_PRE_OUTAGE_BANNER_DISMISSED) === 'true';

const BOLD_TAG = /(<\/?(?:b|strong)>)/i;

export const renderBoldSegments = (text, boldClassName) => {
  let bold = false;
  let position = 0;
  return String(text ?? '')
    .split(BOLD_TAG)
    .map((part) => {
      if (/^<(?:b|strong)>$/i.test(part)) {
        bold = true;
        return null;
      }
      if (/^<\/(?:b|strong)>$/i.test(part)) {
        bold = false;
        return null;
      }
      if (!part) return null;
      position += 1;
      return bold ? (
        <strong key={`bold-${position}`} className={boldClassName}>
          {part}
        </strong>
      ) : (
        part
      );
    });
};

const PreOutageBanner = ({ phoneNumber }) => {
  const classes = useStyles();
  const getDictionary = useDictionary(Dictionary);
  const [dismissed, setDismissed] = useState(isDismissed);

  if (dismissed) return null;

  const text = (entry) => fillPhonePlaceholder(getDictionary(entry.key), phoneNumber);

  const title = text(Dictionary.airPreOutageBannerTitle);
  const messages = [
    Dictionary.airPreOutageBannerWindow,
    Dictionary.airPreOutageBannerAdvice,
    Dictionary.airPreOutageBannerReassurance,
  ]
    .map(({ key }) => ({ key, content: text({ key }) }))
    .filter(({ content }) => content && content.trim());

  const handleClose = () => {
    sessionStorage.setItem(SESSION_STORAGE_PRE_OUTAGE_BANNER_DISMISSED, 'true');
    setDismissed(true);
  };

  return (
    <div className={classes.anchor}>
      <section className={classes.card} aria-label={title} data-testid="pre-outage-banner">
        <svg
          className={classes.icon}
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          focusable="false">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
          <path d="M12 11v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="12" cy="7.6" r="1.1" fill="currentColor" />
        </svg>
        <div className={classes.content}>
          <p className={classes.title}>{title}</p>
          <p className={classes.description}>
            {messages.map(({ key, content }, index) => (
              <React.Fragment key={key}>
                {index > 0 && ' '}
                {key === Dictionary.airPreOutageBannerWindow.key
                  ? renderBoldSegments(content, classes.bold)
                  : content}
              </React.Fragment>
            ))}
          </p>
        </div>
        <button
          type="button"
          className={classes.close}
          aria-label={getDictionary(Dictionary.airPreOutageBannerCloseLabel.key)}
          onClick={handleClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M5 5l14 14M19 5L5 19" stroke="currentColor" strokeWidth="2" />
          </svg>
        </button>
      </section>
    </div>
  );
};

export default PreOutageBanner;
